import { LatLngLiteral } from "leaflet";
import React from "react";
import { useMemo } from "react";
import { Text as BlasterText } from "@blasterjs/core";
import { Circle, Popup } from "react-leaflet";
import {
  AnnotationSource,
  Indication,
  UUID,
  FreehandAnnotation as FreehandAnnotationType
} from "../models";
import { AppDispatch } from "../store";
import AnnotationTooltip from "./AnnotationTooltip";
import { useAppSelector } from "../hooks";
import { isPointInPolygon } from "../utils";

interface Props {
  readonly date: string;
  readonly position: LatLngLiteral;
  readonly annotationId: UUID;
  readonly color: string | null;
  readonly canDelete: boolean;
  readonly weight: number;
  readonly scaleFactor: number;
  readonly hpfId?: UUID;
  readonly pointClassId?: string;
  readonly hpfClassId?: string;
  readonly opacity: number | null;
  readonly _source: AnnotationSource;
  dispatch: AppDispatch;
}

export const defaultPropsCeliac = {
  radius: 1.5,
  fillOpacity: 0.75
};

export const defaultProps = {
  radius: 3,
  color: "black",
  fillColor: "#FFFF00",
  fillOpacity: 1,
  weight: 5,
  zIndex: 999,
  stroke: false
};

const PointAnnotation = ({
  position,
  date,
  annotationId,
  color,
  weight,
  canDelete,
  scaleFactor,
  hpfId,
  pointClassId,
  hpfClassId,
  opacity,
  _source,
  dispatch
}: Props) => {
  const indications = useAppSelector(
    state => state.studyConfiguration.study.data.indications.value
  );

  const defaultRadius = indications.includes(Indication.CeliacDisease)
    ? defaultPropsCeliac.radius
    : defaultProps.radius;
  const defaultOpacity = indications.includes(Indication.CeliacDisease)
    ? defaultPropsCeliac.fillOpacity
    : defaultProps.fillOpacity;
  const pointOpacity = opacity == null ? defaultOpacity : opacity;

  const props = {
    radius: defaultRadius * scaleFactor
  };

  const pathOptions = {
    ...defaultProps,
    weight: 0,
    fillOpacity: pointOpacity,
    strokeOpacity: pointOpacity,
    ...(color ? { fillColor: color } : {})
  };

  const hprops = {
    radius: props.radius * 7 // the multiplier for highlighting
  };

  const hpathOptions = {
    ...defaultProps,
    weight: 1,
    fillOpacity: pointOpacity,
    strokeOpacity: pointOpacity,
    ...(color ? { fillColor: color } : {})
  };

  const manualHpfVisibility = useAppSelector(state => state.caseImageViewer.manualHpfVisibility);
  const aiHpfVisibility = useAppSelector(state => state.caseImageViewer.aiHpfVisibility);
  const imageWithAnnotationsResource = useAppSelector(
    state => state.caseImageViewer.imageWithAnnotations
  );

  const allAnnotations =
    imageWithAnnotationsResource && "resource" in imageWithAnnotationsResource
      ? imageWithAnnotationsResource.resource.annotations
      : [];

  const isAiSource = _source.toLowerCase() === "visiopharm";

  // For AI points, check both visibility arrays; for manual points, only check manual.
  // (aiHpfVisibility can contain Celiac orphan-hide entries that should not affect manual points.)
  const hpfHiddenByManual = manualHpfVisibility.find(
    x =>
      x.hpfClassid === hpfClassId &&
      (x.hpfId === null || (x.hpfId === hpfId && x.pointClassId === null))
  );
  const hpfHiddenByAi = isAiSource
    ? aiHpfVisibility.find(
        x =>
          x.hpfClassid === hpfClassId &&
          (x.hpfId === null || (x.hpfId === hpfId && x.pointClassId === null))
      )
    : undefined;

  // Check if specific point class is hidden (only check matching source)
  const visibilityArray = isAiSource ? aiHpfVisibility : manualHpfVisibility;
  const pointClassHidden = visibilityArray.find(
    x => x.hpfId === hpfId && x.pointClassId === pointClassId
  );

  // For Celiac AI points: only show them when inside a freehand annotation
  const isInsideFreehand = useMemo(() => {
    if (!indications.includes(Indication.CeliacDisease)) {
      return false;
    }

    const freehandAnnotations = allAnnotations.filter(
      (annotation): annotation is FreehandAnnotationType => annotation.geometry.type === "Polygon"
    );

    return freehandAnnotations.some(freehand => {
      return isPointInPolygon(position, freehand.geometry);
    });
  }, [allAnnotations, position, indications]);

  const hidden = isInsideFreehand ? false : hpfHiddenByManual || hpfHiddenByAi || pointClassHidden;

  return hidden ? (
    <></>
  ) : (
    <>
      {weight > 5 ? (
        <Circle center={position} {...hprops} pathOptions={{ ...hpathOptions }}></Circle>
      ) : (
        <></>
      )}
      <Circle center={position} {...props} pathOptions={{ ...pathOptions }}>
        <Popup closeButton={false} className="annotation-popup">
          <AnnotationTooltip annotationId={annotationId} canDelete={canDelete} dispatch={dispatch}>
            <div style={{ paddingTop: "0.2rem", paddingBottom: "0.2rem", lineHeight: "1" }}>
              <BlasterText fontSize="8pt">{date}</BlasterText>
            </div>
          </AnnotationTooltip>
        </Popup>
      </Circle>
    </>
  );
};

export default PointAnnotation;
